import { CheckCircle2, Mail, MapPin, Phone, Tag, Calendar } from "lucide-react";
import { StatusBadge } from "@/components/ui/status-badge";

export type PatronDetailSummaryData = {
  email: string;
  phone: string | null;
  emailVerified: boolean;
  patronAccountStatus: string;
  patronType: { id: string; name: string } | null;
  homeBranch: { id: string; name: string; code: string } | null;
  joinedAt: string;
};

type PatronDetailSummaryProps = {
  detail: PatronDetailSummaryData;
};

export function PatronDetailSummary({ detail }: PatronDetailSummaryProps) {
  const suspended = detail.patronAccountStatus === "SUSPENDED";

  return (
    <div className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
      <div className="flex flex-wrap items-center gap-2">
        <StatusBadge variant={suspended ? "danger" : "success"}>
          {suspended ? "Suspended" : "Active"}
        </StatusBadge>
        {detail.patronType ? (
          <StatusBadge variant="neutral" icon={<Tag className="h-3.5 w-3.5" />}>
            {detail.patronType.name}
          </StatusBadge>
        ) : (
          <StatusBadge variant="warning">No patron type</StatusBadge>
        )}
        {detail.emailVerified && (
          <StatusBadge variant="info" icon={<CheckCircle2 className="h-3.5 w-3.5" />}>
            Email verified
          </StatusBadge>
        )}
      </div>

      <dl className="mt-5 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <div className="min-w-0 space-y-1">
          <dt className="flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-slate-500">
            <Mail className="h-3.5 w-3.5" />
            Email
          </dt>
          <dd className="truncate text-sm text-slate-900" title={detail.email}>
            {detail.email}
          </dd>
        </div>
        <div className="min-w-0 space-y-1">
          <dt className="flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-slate-500">
            <Phone className="h-3.5 w-3.5" />
            Phone
          </dt>
          <dd className="text-sm text-slate-900">{detail.phone?.trim() || "Not set"}</dd>
        </div>
        <div className="min-w-0 space-y-1">
          <dt className="flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-slate-500">
            <MapPin className="h-3.5 w-3.5" />
            Home branch
          </dt>
          <dd className="text-sm text-slate-900">
            {detail.homeBranch ? detail.homeBranch.name : "Not assigned"}
          </dd>
        </div>
        <div className="min-w-0 space-y-1">
          <dt className="flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-slate-500">
            <Calendar className="h-3.5 w-3.5" />
            Joined
          </dt>
          <dd className="text-sm text-slate-900">
            {new Date(detail.joinedAt).toLocaleDateString("en-US", {
              year: "numeric",
              month: "short",
              day: "numeric",
            })}
          </dd>
        </div>
      </dl>
    </div>
  );
}
